import React, { useState, useEffect, useCallback } from 'react';
import {
  getGovtServices,
  getServiceDocuments,
  calculateGovtFee,
  getServiceTimeline,
  getGovtApplications,
  getGovtApplication,
  createGovtApplication,
  updateGovtApplication,
  getGovtDashboard,
  getClients,
  getWorkers
} from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Building2,
  FileText,
  Calculator,
  CheckCircle2,
  AlertCircle,
  Plus,
  RefreshCw,
  Eye,
  Plane,
  Briefcase,
  CreditCard,
  ClipboardList,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '@/lib/utils';

// Sub-components extracted for maintainability
import { GovtDashboardTab } from './govt/GovtDashboardTab';
import { GovtServicesCatalogTab } from './govt/GovtServicesCatalogTab';
import { GovtToolsTab } from './govt/GovtToolsTab';

const statusColors = {
  'Draft': 'bg-slate-100 text-slate-700',
  'Documents Pending': 'bg-amber-100 text-amber-700',
  'Documents Submitted': 'bg-blue-100 text-blue-700',
  'Application Submitted': 'bg-indigo-100 text-indigo-700',
  'Under Processing': 'bg-purple-100 text-purple-700',
  'Approval Pending': 'bg-cyan-100 text-cyan-700',
  'Approved': 'bg-emerald-100 text-emerald-700',
  'Payment Pending': 'bg-orange-100 text-orange-700',
  'Completed': 'bg-green-100 text-green-700',
  'Rejected': 'bg-red-100 text-red-700',
  'Cancelled': 'bg-gray-100 text-gray-700'
};

const deptIcons = {
  ICP: Plane,
  MOHRE: Briefcase,
  EID: CreditCard,
  TASHEEL: Building2
};

const APPLICATION_STATUSES = [
  "Draft", "Documents Pending", "Documents Submitted", "Application Submitted",
  "Under Processing", "Approval Pending", "Approved", "Payment Pending",
  "Completed", "Rejected", "Cancelled"
];

export default function GovtServicesPage() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState({});
  const [applications, setApplications] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [clients, setClients] = useState([]);
  const [workers, setWorkers] = useState([]);

  // Filters
  const [filterDept, setFilterDept] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Dialogs
  const [newAppDialog, setNewAppDialog] = useState(false);
  const [feeCalcDialog, setFeeCalcDialog] = useState(false);
  const [timelineDialog, setTimelineDialog] = useState(false);
  const [docCheckDialog, setDocCheckDialog] = useState(false);
  const [viewAppDialog, setViewAppDialog] = useState(false);

  // Dialog data
  const [selectedService, setSelectedService] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const [feeResult, setFeeResult] = useState(null);
  const [timelineResult, setTimelineResult] = useState(null);
  const [docChecklist, setDocChecklist] = useState([]);

  // New Application form
  const [newApp, setNewApp] = useState({
    department: '', service_code: '', client_id: '', worker_id: '',
    applicant_name: '', applicant_passport: '', applicant_nationality: '', notes: ''
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [svcRes, dashRes, clientsRes, workersRes] = await Promise.all([
        getGovtServices(),
        getGovtDashboard(),
        getClients({ limit: 500 }),
        getWorkers({ limit: 500 })
      ]);
      setServices(svcRes.data);
      setDashboard(dashRes.data);
      setClients(clientsRes.data.items || []);
      setWorkers(workersRes.data.items || []);
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadApplications = useCallback(async () => {
    try {
      const params = { limit: 100 };
      if (filterDept) params.department = filterDept;
      if (filterStatus) params.status = filterStatus;
      const res = await getGovtApplications(params);
      setApplications(res.data.items || []);
    } catch (error) {
      toast.error('Failed to load applications');
    }
  }, [filterDept, filterStatus]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadApplications(); }, [loadApplications]);

  const handleCalculateFee = useCallback(async (serviceCode, nationality = 'default', urgent = false) => {
    try {
      const res = await calculateGovtFee({ service_code: serviceCode, nationality, urgent });
      setFeeResult(res.data);
      setFeeCalcDialog(true);
    } catch (error) {
      toast.error('Failed to calculate fee');
    }
  }, []);

  const handleGetTimeline = useCallback(async (serviceCode, urgent = false) => {
    try {
      const res = await getServiceTimeline(serviceCode, urgent);
      setTimelineResult(res.data);
      setTimelineDialog(true);
    } catch (error) {
      toast.error('Failed to get timeline');
    }
  }, []);

  const handleGetDocuments = useCallback(async (serviceCode) => {
    try {
      const res = await getServiceDocuments(serviceCode);
      setDocChecklist(res.data.required_documents || []);
      setDocCheckDialog(true);
    } catch (error) {
      toast.error('Failed to get document checklist');
    }
  }, []);

  const handleCreateApplication = async () => {
    if (!newApp.department || !newApp.service_code || !newApp.client_id || !newApp.applicant_name) {
      toast.error('Please fill all required fields');
      return;
    }
    setLoading(true);
    try {
      await createGovtApplication(newApp);
      toast.success('Application created successfully');
      setNewAppDialog(false);
      setNewApp({ department: '', service_code: '', client_id: '', worker_id: '', applicant_name: '', applicant_passport: '', applicant_nationality: '', notes: '' });
      loadApplications();
      loadData();
    } catch (error) {
      toast.error('Failed to create application');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (appId, newStatus) => {
    try {
      await updateGovtApplication(appId, { status: newStatus });
      toast.success('Status updated');
      loadApplications();
      if (selectedApp?.id === appId) {
        const res = await getGovtApplication(appId);
        setSelectedApp(res.data);
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleViewApplication = async (app) => {
    try {
      const res = await getGovtApplication(app.id);
      setSelectedApp(res.data);
      setViewAppDialog(true);
    } catch (error) {
      toast.error('Failed to load application details');
    }
  };

  const getAvailableServices = () => {
    if (!newApp.department || !services[newApp.department]) return [];
    return services[newApp.department].services || [];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" data-testid="govt-services-title">Government Services</h1>
          <p className="text-muted-foreground">UAE Immigration, Labour, Emirates ID & Tasheel Services</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Dialog open={newAppDialog} onOpenChange={setNewAppDialog}>
            <DialogTrigger asChild>
              <Button data-testid="new-application-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Application
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>New Government Application</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <Label>Department *</Label>
                  <Select value={newApp.department} onValueChange={(v) => setNewApp(p => ({ ...p, department: v, service_code: '' }))}>
                    <SelectTrigger data-testid="select-department"><SelectValue placeholder="Select department" /></SelectTrigger>
                    <SelectContent>{Object.entries(services).map(([code, data]) => (<SelectItem key={code} value={code}>{data.name}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Service *</Label>
                  <Select value={newApp.service_code} onValueChange={(v) => setNewApp(p => ({ ...p, service_code: v }))} disabled={!newApp.department}>
                    <SelectTrigger data-testid="select-service"><SelectValue placeholder="Select service" /></SelectTrigger>
                    <SelectContent>{getAvailableServices().map((svc) => (<SelectItem key={svc.code} value={svc.code}>{svc.name}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Client *</Label>
                  <Select value={newApp.client_id} onValueChange={(v) => setNewApp(p => ({ ...p, client_id: v }))}>
                    <SelectTrigger data-testid="select-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                    <SelectContent>{clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.display_name || c.company_name}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Worker (Optional)</Label>
                  <Select value={newApp.worker_id || "none"} onValueChange={(v) => setNewApp(p => ({ ...p, worker_id: v === "none" ? "" : v }))}>
                    <SelectTrigger><SelectValue placeholder="Select worker" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {workers.map((w) => (<SelectItem key={w.id} value={w.id}>{w.full_name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Applicant Name *</Label>
                  <Input value={newApp.applicant_name} onChange={(e) => setNewApp(p => ({ ...p, applicant_name: e.target.value }))} placeholder="Full name as per passport" data-testid="applicant-name" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Passport Number</Label><Input value={newApp.applicant_passport} onChange={(e) => setNewApp(p => ({ ...p, applicant_passport: e.target.value }))} placeholder="e.g. AB1234567" /></div>
                  <div><Label>Nationality</Label><Input value={newApp.applicant_nationality} onChange={(e) => setNewApp(p => ({ ...p, applicant_nationality: e.target.value }))} placeholder="e.g. Indian" /></div>
                </div>
                <div><Label>Notes</Label><Textarea value={newApp.notes} onChange={(e) => setNewApp(p => ({ ...p, notes: e.target.value }))} rows={2} /></div>
                <Button className="w-full" onClick={handleCreateApplication} disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  Create Application
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-4 w-full max-w-xl">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard"><Building2 className="w-4 h-4 mr-1" /> Dashboard</TabsTrigger>
          <TabsTrigger value="services" data-testid="tab-services"><ClipboardList className="w-4 h-4 mr-1" /> Services</TabsTrigger>
          <TabsTrigger value="applications" data-testid="tab-applications"><FileText className="w-4 h-4 mr-1" /> Applications</TabsTrigger>
          <TabsTrigger value="tools" data-testid="tab-tools"><Calculator className="w-4 h-4 mr-1" /> Tools</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-6">
          <GovtDashboardTab loading={loading} dashboard={dashboard} services={services} deptIcons={deptIcons} statusColors={statusColors} />
        </TabsContent>

        <TabsContent value="services" className="mt-6">
          <GovtServicesCatalogTab services={services} deptIcons={deptIcons} onCalculateFee={handleCalculateFee} onGetTimeline={handleGetTimeline} onGetDocuments={handleGetDocuments} onSelectService={setSelectedService} />
        </TabsContent>

        <TabsContent value="applications" className="mt-6">
          <Card>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Government Applications</h3>
                <div className="flex gap-2">
                  <Select value={filterDept} onValueChange={(v) => setFilterDept(v === 'all' ? '' : v)}>
                    <SelectTrigger className="w-[150px]"><SelectValue placeholder="All Departments" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Departments</SelectItem>
                      {Object.entries(services).map(([code, data]) => (<SelectItem key={code} value={code}>{data.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
                    <SelectTrigger className="w-[150px]"><SelectValue placeholder="All Statuses" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      {APPLICATION_STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {applications.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Application #</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Applicant</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Est. Completion</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {applications.map((app) => (
                      <TableRow key={app.id} data-testid={`app-row-${app.id}`}>
                        <TableCell className="font-mono">{app.application_number}</TableCell>
                        <TableCell><Badge variant="outline">{app.department}</Badge></TableCell>
                        <TableCell className="max-w-[150px] truncate">{app.service_name}</TableCell>
                        <TableCell>{app.applicant_name}</TableCell>
                        <TableCell>{app.client_name || '-'}</TableCell>
                        <TableCell><Badge className={statusColors[app.status]}>{app.status}</Badge></TableCell>
                        <TableCell>{formatDate(app.estimated_completion)}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => handleViewApplication(app)}><Eye className="w-4 h-4" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-20" />
                  <p>No applications found</p>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="tools" className="mt-6">
          <GovtToolsTab services={services} onCalculateFee={handleCalculateFee} onGetTimeline={handleGetTimeline} onGetDocuments={handleGetDocuments} />
        </TabsContent>
      </Tabs>

      {/* Fee Calculation Result Dialog */}
      <Dialog open={feeCalcDialog} onOpenChange={setFeeCalcDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Fee Calculation Result</DialogTitle></DialogHeader>
          {feeResult && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-semibold">{feeResult.service_name}</h4>
                <p className="text-sm text-muted-foreground">{feeResult.department}</p>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between"><span>Base Fee:</span><span className="font-mono">{formatCurrency(feeResult.base_fee)}</span></div>
                {feeResult.nationality !== 'default' && (<div className="flex justify-between text-sm text-muted-foreground"><span>Nationality Modifier:</span><span>{feeResult.nationality_modifier}x</span></div>)}
                <div className="flex justify-between"><span>Government Fee:</span><span className="font-mono">{formatCurrency(feeResult.govt_fee)}</span></div>
                <div className="flex justify-between"><span>VAT (5%):</span><span className="font-mono">{formatCurrency(feeResult.vat_amount)}</span></div>
                <div className="flex justify-between border-t pt-2 font-semibold"><span>Total:</span><span className="font-mono text-lg">{formatCurrency(feeResult.total_fee)}</span></div>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg text-sm">
                <p><strong>Processing Time:</strong> {feeResult.processing_days} working days</p>
                <p><strong>Estimated Completion:</strong> {formatDate(feeResult.estimated_completion)}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Timeline Dialog */}
      <Dialog open={timelineDialog} onOpenChange={setTimelineDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Application Timeline</DialogTitle></DialogHeader>
          {timelineResult && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-semibold">{timelineResult.service_name}</h4>
                <p className="text-sm text-muted-foreground">Total: {timelineResult.total_days} days | {timelineResult.urgent ? 'Urgent' : 'Standard'}</p>
              </div>
              <div className="space-y-3">
                {timelineResult.timeline?.map((step) => (
                  <div key={step.step} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center font-semibold text-sm">{step.step}</div>
                    <div className="flex-1">
                      <p className="font-medium">{step.milestone}</p>
                      <p className="text-sm text-muted-foreground">{step.description}</p>
                      <p className="text-xs text-muted-foreground mt-1">{step.start_date} → {step.end_date} ({step.duration_days} day{step.duration_days > 1 ? 's' : ''})</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-3 bg-emerald-50 rounded-lg">
                <p className="font-semibold text-emerald-700">Expected Completion: {formatDate(timelineResult.estimated_completion)}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Document Checklist Dialog */}
      <Dialog open={docCheckDialog} onOpenChange={setDocCheckDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Required Documents</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {docChecklist.map((doc, idx) => (
              <div key={doc} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Checkbox id={`doc-${idx}`} />
                <Label htmlFor={`doc-${idx}`} className="cursor-pointer">{doc}</Label>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* View Application Dialog */}
      <Dialog open={viewAppDialog} onOpenChange={setViewAppDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Application Details</DialogTitle></DialogHeader>
          {selectedApp && (
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-mono text-lg font-semibold">{selectedApp.application_number}</p>
                  <p className="text-sm text-muted-foreground">{selectedApp.service_name}</p>
                </div>
                <Badge className={`${statusColors[selectedApp.status]} text-sm px-3 py-1`}>{selectedApp.status}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><Label className="text-muted-foreground">Department</Label><p className="font-medium">{selectedApp.department_name}</p></div>
                <div><Label className="text-muted-foreground">Applicant</Label><p className="font-medium">{selectedApp.applicant_name}</p></div>
                <div><Label className="text-muted-foreground">Passport</Label><p className="font-medium">{selectedApp.applicant_passport || '-'}</p></div>
                <div><Label className="text-muted-foreground">Nationality</Label><p className="font-medium">{selectedApp.applicant_nationality || '-'}</p></div>
                <div><Label className="text-muted-foreground">Client</Label><p className="font-medium">{selectedApp.client_name || '-'}</p></div>
                <div><Label className="text-muted-foreground">Reference #</Label><p className="font-mono">{selectedApp.reference_number || selectedApp.govt_reference || '-'}</p></div>
                <div><Label className="text-muted-foreground">Government Fee</Label><p className="font-mono font-semibold">{formatCurrency(selectedApp.govt_fee)}</p></div>
                <div><Label className="text-muted-foreground">Service Fee</Label><p className="font-mono font-semibold">{formatCurrency(selectedApp.service_fee)}</p></div>
              </div>
              <div>
                <Label>Update Status</Label>
                <Select onValueChange={(v) => handleUpdateStatus(selectedApp.id, v)}>
                  <SelectTrigger><SelectValue placeholder="Select new status" /></SelectTrigger>
                  <SelectContent>{APPLICATION_STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              {selectedApp.status_history?.length > 0 && (
                <div>
                  <Label className="text-muted-foreground">Status History</Label>
                  <div className="mt-2 space-y-2">
                    {selectedApp.status_history.map((h, idx) => (
                      <div key={`${h.status}-${h.date}`} className="flex items-center gap-2 text-sm">
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        <Badge variant="outline" className="text-xs">{h.status}</Badge>
                        <span className="text-muted-foreground">{formatDate(h.date)}</span>
                        <span className="text-muted-foreground">by {h.user}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {selectedApp.documents_required?.length > 0 && (
                <div>
                  <Label className="text-muted-foreground">Documents Required</Label>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {selectedApp.documents_required.map((doc) => {
                      const isCollected = selectedApp.documents_collected?.includes(doc);
                      return (
                        <div key={doc} className={`flex items-center gap-2 p-2 rounded text-sm ${isCollected ? 'bg-emerald-50' : 'bg-slate-50'}`}>
                          {isCollected ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertCircle className="w-4 h-4 text-slate-400" />}
                          <span>{doc}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
